import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { rockKindColor } from './palette';
import type { SceneLayers } from './scene';

const halfW = TILE_WIDTH_PX / 2;
const halfH = TILE_HEIGHT_PX / 2;

// ─── Cave tile renderer ──────────────────────────────────────────────────

export interface CaveTileRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    localZLevel: number,
  ) => void;
  destroy: () => void;
}

export function createCaveTileRenderer(layers: SceneLayers): CaveTileRenderer {
  const g = new Graphics();
  layers.caveTileLayer.addChild(g);

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    localZLevel: number,
  ): void => {
    if (localZLevel !== -1 || model.caveTiles.size === 0) {
      g.visible = false;
      return;
    }
    g.visible = true;
    g.clear();

    const r = visibleTileRange(cam, viewportW, viewportH, 2);

    for (const [, ct] of model.caveTiles) {
      const [cx, cy] = ct.position;
      if (cx < r.minX || cx > r.maxX || cy < r.minY || cy > r.maxY) continue;
      const color = rockKindColor(ct.rock_type);
      const { x: px, y: py } = tileToIso(cx, cy);
      g.poly([px, py - halfH, px + halfW, py, px, py + halfH, px - halfW, py]);
      g.fill({ color, alpha: 1 });
    }
  };

  return { render, destroy: () => g.destroy() };
}

// ─── Cave mouth marker renderer ──────────────────────────────────────────

const CAVE_MOUTH_COLOR = 0x4a4a4a;
const CAVE_MOUTH_ALPHA = 0.6;

export interface CaveMouthRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    localZLevel: number,
  ) => void;
  destroy: () => void;
}

export function createCaveMouthRenderer(layers: SceneLayers): CaveMouthRenderer {
  const g = new Graphics();
  layers.floorOverlayLayer.addChild(g);

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    localZLevel: number,
  ): void => {
    if (localZLevel !== 0 || model.caveMouths.length === 0) {
      g.visible = false;
      return;
    }
    g.visible = true;
    g.clear();

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const markerHW = halfW * 0.4;
    const markerHH = halfH * 0.4;

    for (const mouth of model.caveMouths) {
      const [mx, my] = mouth.position;
      if (mx < r.minX || mx > r.maxX || my < r.minY || my > r.maxY) continue;
      const { x: px, y: py } = tileToIso(mx, my);
      g.poly([px, py - markerHH, px + markerHW, py, px, py + markerHH, px - markerHW, py]);
      g.fill({ color: CAVE_MOUTH_COLOR, alpha: CAVE_MOUTH_ALPHA });
      g.stroke({ color: CAVE_MOUTH_COLOR, width: 1.5, alpha: 0.8 });
    }
  };

  return { render, destroy: () => g.destroy() };
}
